import React, { useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import {
  useCreateForm,
  useGetFetch,
  useUpdateForm,
} from "../../../../data/Queries";
import {
  addonCategories,
  addonCategoryEndPoint,
  createAddonEndPoint,
  draftAddons,
  trainerEndpoint,
  trainerLevelEndpoint,
  trainerLevels,
  updateAddonEndPoint,
} from "../../../../data/ApiEndpoints";
import { INTEGER_REGEX } from "../../../../data/Constants";
import CommonHeadingNBtns from "../../../common-components/CommonHeadingNBtns";
import SelectDropdown from "../../../components-react/SelectDropdown";
import ReactQuillTextEditor from "../../../components-react/inputs/ReactQuillTextEditor";
import "../../../../styles/centralised-plan/centralised-pt-plans.css";

const CentralisedPTForm = ({
  userInfoData,
  rowData,
  isEdit,
  closeModal,
  callFunction,
}) => {
  const token = userInfoData?.token;
  const isDisabled = false;
  const refs = {};

  const iniFormValues = {
    planName: rowData?.addon_name || "",
    planCategory: null,
    customerLevel: null,
    intensity: null,
    weekdays: null,
    description: rowData?.description || "",
    isTrial: false,
    sessions: [
      {
        isTrialSession: false,
        trainerLevel: null,
        numSessions: "",
        duration: "",
        basePrice: "",
        taxPercentage: "18",
        finalBasePrice: "",
        sellingPrices: [
          {
            finalSellingPrice: "",
            taxPercentage: "18",
            basePrice: "",
          },
        ],
      },
    ],
  };

  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdditionalSellingPrices, setShowAdditionalSellingPrices] =
    useState(false);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [expandedPrices, setExpandedPrices] = useState({});
  const [allSessionsExpanded, setAllSessionsExpanded] = useState(true);

  // PT plan category options from API
  const { data: categoryData, isLoading: isLoadingCategories } = useGetFetch({
    url: addonCategoryEndPoint,
    filters: { status: "active" },
    token,
    key: addonCategories,
  });

  // Experience level data (trainer levels)
  const { data: trainerLevelData, isLoading: isLoadingTrainerLevel } =
    useGetFetch({
      url: `${trainerEndpoint}${trainerLevelEndpoint}`,
      token,
      key: trainerLevels,
    });

  // Create PT plan
  const { mutateAsync: createPTPlan, isLoading: isLoadingCreatePT } =
    useCreateForm({
      url: createAddonEndPoint,
      key: draftAddons,
      token,
    });

  // Update PT plan
  const { mutateAsync: updatePTPlan, isLoading: isLoadingUpdatePT } =
    useUpdateForm({
      url: updateAddonEndPoint,
      key: draftAddons,
      token,
    });

  // Intensity options
  const intensityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" },
  ];

  // Weekday options
  const weekdaysOptions = [
    { label: "Monday", value: "monday" },
    { label: "Tuesday", value: "tuesday" },
    { label: "Wednesday", value: "wednesday" },
    { label: "Thursday", value: "thursday" },
    { label: "Friday", value: "friday" },
    { label: "Saturday", value: "saturday" },
    { label: "Sunday", value: "sunday" },
  ];

  // Customer level options
  const customerLevelOptions = [
    { label: "Beginner", value: "beginner" },
    { label: "Intermediate", value: "intermediate" },
    { label: "Advanced", value: "advanced" },
  ];

  // Utility function to assign refs
  const assignRef = (name) => {
    if (!refs[name]) {
      refs[name] = React.createRef();
    }
    return refs[name];
  };

  const onChangeInput = (e) => {
    if (isDisabled) return;

    const { name, value, type, checked } = e.target;
    setIsSubmit(false);

    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeDropdown = (value, name) => {
    if (isDisabled) return;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const errors = {};

    if (!formValues.planName.trim()) {
      errors.planName = "Plan name is required";
    }

    if (!formValues.planCategory) {
      errors.planCategory = "PT plan category is required";
    }

    if (!formValues.customerLevel) {
      errors.customerLevel = "Customer level is required";
    }

    if (!formValues.intensity) {
      errors.intensity = "Intensity is required";
    }

    if (!formValues.weekdays || formValues.weekdays.length === 0) {
      errors.weekdays = "Weekdays are required";
    }

    if (!formValues.description.trim() || formValues.description.length < 15) {
      errors.description = "Description must be at least 15 characters";
    }

    // Validate sessions
    const sessionErrors = [];
    formValues.sessions.forEach((session, index) => {
      const sError = {};

      if (!session.trainerLevel) {
        sError.trainerLevel = "Trainer level is required";
      }

      if (!session.numSessions || !INTEGER_REGEX.test(session.numSessions)) {
        sError.numSessions = "Valid number of sessions is required";
      }

      if (!session.duration || !INTEGER_REGEX.test(session.duration)) {
        sError.duration = "Valid duration is required";
      }

      if (!session.basePrice || !INTEGER_REGEX.test(session.basePrice)) {
        sError.basePrice = "Valid base price is required";
      }

      // Validate selling prices
      const spErrors = [];
      session.sellingPrices.forEach((sp, spIndex) => {
        const spError = {};

        if (
          !sp.finalSellingPrice ||
          !INTEGER_REGEX.test(sp.finalSellingPrice)
        ) {
          spError.finalSellingPrice = "Valid selling price is required";
        }

        if (Object.keys(spError).length > 0) {
          spErrors[spIndex] = spError;
        }
      });

      if (spErrors.length > 0) {
        sError.sellingPrices = spErrors;
      }

      if (Object.keys(sError).length > 0) {
        sessionErrors[index] = sError;
      }
    });

    if (sessionErrors.length > 0) {
      errors.sessions = sessionErrors;
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmit(true);
      setIsLoading(true);

      try {
        const formData = {
          addon_name: formValues.planName,
          addon_category: formValues.planCategory?.value,
          addon_for: formValues.customerLevel?.value,
          intensity: formValues.intensity?.value,
          addon_days: formValues.weekdays?.map((day) => day.value),
          description: formValues.description,
          is_trial: formValues.isTrial,
          sessions: formValues.sessions.map((session) => ({
            is_trial: session.isTrialSession,
            trainer_exp_lvl: session.trainerLevel?.value,
            num_session: session.numSessions,
            duration: session.duration,
            base_price: session.basePrice,
            tax_percentage: session.taxPercentage,
            final_base_price: calculateFinalBasePrice(
              session.basePrice,
              session.taxPercentage
            ),
            selling_prices: session.sellingPrices.map((sp) => ({
              final_selling_price: sp.finalSellingPrice,
              tax_percentage: sp.taxPercentage,
              base_price: calculateBasePrice(
                sp.finalSellingPrice,
                sp.taxPercentage
              ),
            })),
          })),
        };

        if (isEdit && rowData?.uid) {
          formData.uid = rowData.uid;
          await updatePTPlan(formData);
          toast.success("PT Plan updated successfully");
        } else {
          await createPTPlan(formData);
          toast.success("PT Plan created successfully");
        }

        callFunction();
        closeModal();
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(error?.response?.data?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
        setIsSubmit(false);
      }
    }
  };

  const calculateFinalBasePrice = (basePrice, taxPercentage) => {
    if (!basePrice || !taxPercentage) return "";
    const base = parseFloat(basePrice);
    const tax = parseFloat(taxPercentage);
    return Math.round(base + (base * tax) / 100).toString();
  };

  const calculateBasePrice = (finalPrice, taxPercentage) => {
    if (!finalPrice || !taxPercentage) return "";
    const final = parseFloat(finalPrice);
    const tax = parseFloat(taxPercentage);
    return Math.round(final / (1 + tax / 100)).toString();
  };

  const handleSessionChange = (index, name, value) => {
    const updatedSessions = [...formValues.sessions];
    updatedSessions[index] = {
      ...updatedSessions[index],
      [name]: value,
    };

    // Calculate final base price if base price is updated
    if (name === "basePrice" || name === "taxPercentage") {
      const basePrice =
        name === "basePrice" ? value : updatedSessions[index].basePrice;
      const taxPercentage =
        name === "taxPercentage" ? value : updatedSessions[index].taxPercentage;
      if (basePrice && taxPercentage) {
        updatedSessions[index].finalBasePrice = calculateFinalBasePrice(
          basePrice,
          taxPercentage
        );
      }
    }

    setFormValues({
      ...formValues,
      sessions: updatedSessions,
    });
  };

  const handleSellingPriceChange = (sessionIndex, spIndex, name, value) => {
    const updatedSessions = [...formValues.sessions];
    updatedSessions[sessionIndex].sellingPrices[spIndex] = {
      ...updatedSessions[sessionIndex].sellingPrices[spIndex],
      [name]: value,
    };

    // Calculate base price if final selling price is updated
    if (name === "finalSellingPrice" || name === "taxPercentage") {
      const finalPrice =
        name === "finalSellingPrice"
          ? value
          : updatedSessions[sessionIndex].sellingPrices[spIndex]
              .finalSellingPrice;
      const taxPercentage =
        name === "taxPercentage"
          ? value
          : updatedSessions[sessionIndex].sellingPrices[spIndex].taxPercentage;

      if (finalPrice && taxPercentage) {
        updatedSessions[sessionIndex].sellingPrices[spIndex].basePrice =
          calculateBasePrice(finalPrice, taxPercentage);
      }
    }

    setFormValues({
      ...formValues,
      sessions: updatedSessions,
    });
  };

  const addSession = () => {
    setFormValues({
      ...formValues,
      sessions: [
        ...formValues.sessions,
        {
          isTrialSession: false,
          trainerLevel: null,
          numSessions: "",
          duration: "",
          basePrice: "",
          taxPercentage: "18",
          finalBasePrice: "",
          sellingPrices: [
            {
              finalSellingPrice: "",
              taxPercentage: "18",
              basePrice: "",
            },
          ],
        },
      ],
    });
  };

  const removeSession = (index) => {
    if (formValues.sessions.length > 1) {
      const updatedSessions = [...formValues.sessions];
      updatedSessions.splice(index, 1);
      setFormValues({
        ...formValues,
        sessions: updatedSessions,
      });
    }
  };

  const addSellingPrice = (sessionIndex) => {
    const updatedSessions = [...formValues.sessions];
    updatedSessions[sessionIndex].sellingPrices.push({
      finalSellingPrice: "",
      taxPercentage: "18",
      basePrice: "",
    });
    setFormValues({
      ...formValues,
      sessions: updatedSessions,
    });
  };

  const removeSellingPrice = (sessionIndex, spIndex) => {
    if (formValues.sessions[sessionIndex].sellingPrices.length > 1) {
      const updatedSessions = [...formValues.sessions];
      updatedSessions[sessionIndex].sellingPrices.splice(spIndex, 1);
      setFormValues({
        ...formValues,
        sessions: updatedSessions,
      });
    }
  };

  const toggleSession = (sessionIndex) => {
    setExpandedSessions((prev) => {
      const newState = {
        ...prev,
        [sessionIndex]: !prev[sessionIndex],
      };

      // Check if all sessions are now in the same state
      const allExpanded = Object.values(newState).every((val) => val !== false);
      const allCollapsed = Object.values(newState).every(
        (val) => val === false
      );

      if (allExpanded !== allSessionsExpanded && allExpanded) {
        setAllSessionsExpanded(true);
      } else if (allCollapsed) {
        setAllSessionsExpanded(false);
      }

      return newState;
    });
  };

  const toggleSellingPrice = (sessionIndex, priceIndex) => {
    const key = `${sessionIndex}-${priceIndex}`;
    setExpandedPrices((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleAllSessions = () => {
    const newExpandedState = !allSessionsExpanded;
    setAllSessionsExpanded(newExpandedState);

    // Create a new object with all sessions set to the same expansion state
    const updatedExpandedSessions = {};
    formValues.sessions.forEach((_, index) => {
      updatedExpandedSessions[index] = newExpandedState;
    });

    setExpandedSessions(updatedExpandedSessions);
  };

  useEffect(() => {
    if (categoryData?.data) {
      const formattedCategories = categoryData.data.map((item) => ({
        label: item.name,
        value: item.uid,
      }));

      if (rowData?.addon_category) {
        const selectedCategory = formattedCategories.find(
          (cat) => cat.value === rowData.addon_category
        );
        setFormValues((prev) => ({
          ...prev,
          planCategory: selectedCategory || null,
        }));
      }
    }
  }, [categoryData, rowData]);

  useEffect(() => {
    if (trainerLevelData?.data) {
      const formattedLevels = trainerLevelData.data.map((item) => ({
        label: item.name,
        value: item.id,
      }));

      if (rowData?.sessions && rowData.sessions.length > 0) {
        const updatedSessions = [...formValues.sessions];
        rowData.sessions.forEach((session, index) => {
          if (index < updatedSessions.length) {
            const selectedLevel = formattedLevels.find(
              (lvl) => lvl.value === session.trainer_exp_lvl
            );
            updatedSessions[index].trainerLevel = selectedLevel || null;
            updatedSessions[index].numSessions = session.num_session || "";
            updatedSessions[index].duration = session.duration || "";
            updatedSessions[index].basePrice = session.base_price || "";
            updatedSessions[index].finalBasePrice =
              session.final_base_price || "";
            updatedSessions[index].isTrialSession = session.is_trial || false;
          }
        });

        setFormValues((prev) => ({
          ...prev,
          sessions: updatedSessions,
        }));
      }
    }
  }, [trainerLevelData, rowData]);

  // Set default values if editing
  useEffect(() => {
    if (isEdit && rowData) {
      // Set customer level
      if (rowData.addon_for) {
        const level = customerLevelOptions.find(
          (opt) => opt.value === rowData.addon_for
        );
        setFormValues((prev) => ({
          ...prev,
          customerLevel: level || null,
        }));
      }

      // Set intensity
      if (rowData.intensity) {
        const intensity = intensityOptions.find(
          (opt) => opt.value === rowData.intensity
        );
        setFormValues((prev) => ({
          ...prev,
          intensity: intensity || null,
        }));
      }

      // Set weekdays
      if (rowData.addon_days && Array.isArray(rowData.addon_days)) {
        const selected = weekdaysOptions.filter((day) =>
          rowData.addon_days.includes(day.value)
        );
        setFormValues((prev) => ({
          ...prev,
          weekdays: selected || null,
        }));
      }

      // Set description
      setFormValues((prev) => ({
        ...prev,
        description: rowData.description || "",
        isTrial: rowData.is_trial || false,
      }));
    }
  }, [isEdit, rowData]);

  return (
    <div className="form-container open-form-page-container">
      <CommonHeadingNBtns
        title="Centralised PT Plan Form"
        btnTxt={isEdit ? "Update PT Plan" : "Add PT Plan"}
        onClose={closeModal}
        onClick={handleSubmit}
        isLoading={isLoading}
        isDisabled={isSubmit}
      />

      <div className="formContainer" style={{ marginTop: "30px" }}>
        <div className="formInputsGrid fourGridSystem scroll-container">
          {/* Plan Name */}
          <div className="formInputBox">
            <label htmlFor="planName" className="formInputLabel">
              Plan Name <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Plan Name"
              className={`formInput ${formErrors.planName ? "formError" : ""}`}
              name="planName"
              value={formValues.planName}
              onChange={onChangeInput}
              ref={assignRef("planName")}
              disabled={isDisabled}
            />
            <span className="error-label">{formErrors.planName || ""}</span>
          </div>

          {/* Plan Category Dropdown */}
          <div className="formInputBox">
            <label htmlFor="planCategory" className="formInputLabel">
              PT Plan Category <span className="required">*</span>
            </label>
            <div
              className={`commonWrapper ${
                formErrors.planCategory ? "formError" : ""
              }`}
              ref={assignRef("planCategory")}>
              <SelectDropdown
                options={
                  categoryData?.data?.map((cat) => ({
                    label: cat.name,
                    value: cat.uid,
                  })) || []
                }
                selectedOption={formValues.planCategory}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "planCategory")
                }
                name="PT Plan Category"
                isLoading={isLoadingCategories}
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">{formErrors.planCategory || ""}</span>
          </div>

          {/* Customer Level */}
          <div className="formInputBox">
            <label htmlFor="customerLevel" className="formInputLabel">
              Customer Level <span className="required">*</span>
            </label>
            <div
              className={`commonWrapper ${
                formErrors.customerLevel ? "formError" : ""
              }`}
              ref={assignRef("customerLevel")}>
              <SelectDropdown
                options={customerLevelOptions}
                selectedOption={formValues.customerLevel}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "customerLevel")
                }
                name="Customer Level"
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">
              {formErrors.customerLevel || ""}
            </span>
          </div>

          {/* Intensity */}
          <div className="formInputBox">
            <label htmlFor="intensity" className="formInputLabel">
              Intensity <span className="required">*</span>
            </label>
            <div
              className={`commonWrapper ${
                formErrors.intensity ? "formError" : ""
              }`}
              ref={assignRef("intensity")}>
              <SelectDropdown
                options={intensityOptions}
                selectedOption={formValues.intensity}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "intensity")
                }
                name="Intensity"
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">{formErrors.intensity || ""}</span>
          </div>

          {/* Weekdays */}
          <div className="formInputBox">
            <label htmlFor="weekdays" className="formInputLabel">
              Weekdays <span className="required">*</span>
            </label>
            <div
              className={`commonWrapper ${
                formErrors.weekdays ? "formError" : ""
              }`}
              ref={assignRef("weekdays")}>
              <SelectDropdown
                options={weekdaysOptions}
                selectedOption={formValues.weekdays}
                setSelectedOption={(value) =>
                  onChangeDropdown(value, "weekdays")
                }
                name="Weekdays"
                isMulti={true}
                isDisable={isDisabled}
              />
            </div>
            <span className="error-label">{formErrors.weekdays || ""}</span>
          </div>

          {/* Is Trial */}
          <div className="formInputBox checkbox-wrapper">
            <div className="flex-box">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isTrial"
                  checked={formValues.isTrial}
                  onChange={onChangeInput}
                  disabled={isDisabled}
                />
                Is Trial
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="formInputBox span4 description-section">
            <label htmlFor="description" className="formInputLabel">
              Description <span className="required">*</span>
            </label>
            <div className="commonWrapper textAreaCommonWrapper with-auto-height no-border">
              <ReactQuillTextEditor
                value={formValues.description}
                setValue={(content) =>
                  setFormValues((prev) => ({ ...prev, description: content }))
                }
                isError={formErrors?.description ? "formError" : ""}
                disabled={isDisabled}
              />
            </div>
            <span className="error-label">{formErrors.description || ""}</span>
          </div>

          {/* Sessions */}
          <div className="formInputBox span4">
            <div className="section-container">
              <div className="section-header">
                <div className="section-title-wrap">
                  <h4 className="section-title">Sessions</h4>
                  <span className="session-count">
                    {formValues.sessions.length}
                  </span>
                </div>
                <div className="section-actions">
                  <button
                    type="button"
                    className="toggle-all-btn"
                    onClick={toggleAllSessions}>
                    {allSessionsExpanded ? (
                      <>
                        Collapse All Sessions ({formValues.sessions.length}){" "}
                        <span className="arrow">▲</span>
                      </>
                    ) : (
                      <>
                        Expand All Sessions ({formValues.sessions.length}){" "}
                        <span className="arrow">▼</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="add-btn"
                    onClick={addSession}
                    disabled={isDisabled}>
                    <span>+ Add Session</span>
                  </button>
                </div>
              </div>

              {formValues.sessions.map((session, sessionIndex) => (
                <div key={sessionIndex} className="session-container">
                  <div
                    className="session-header"
                    onClick={() => toggleSession(sessionIndex)}>
                    <div className="session-header-left">
                      <div className="session-index">
                        Session {sessionIndex + 1}
                      </div>
                      <div className="session-summary">
                        {session.trainerLevel?.label ? (
                          <span className="trainer-level">
                            {session.trainerLevel.label}
                          </span>
                        ) : (
                          <span className="no-trainer">
                            No trainer selected
                          </span>
                        )}
                        <span className="session-details">
                          {session.numSessions || "0"} sessions •{" "}
                          {session.duration || "0"} min • ₹
                          {session.basePrice || "0"}
                        </span>
                      </div>
                    </div>
                    <div className="session-header-right">
                      {formValues.sessions.length > 1 && (
                        <button
                          className="remove-icon"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSession(sessionIndex);
                          }}
                          disabled={isDisabled}>
                          <span>×</span>
                        </button>
                      )}
                      <div className="collapse-icon">
                        {expandedSessions[sessionIndex] ? "▲" : "▼"}
                      </div>
                    </div>
                  </div>

                  {expandedSessions[sessionIndex] !== false && (
                    <div className="session-body">
                      <div className="form-row two-columns">
                        <div className="form-group">
                          <label>
                            Trainer Level{" "}
                            <span className="required-mark">*</span>
                          </label>
                          <div
                            className={`select-wrapper ${
                              formErrors.sessions?.[sessionIndex]?.trainerLevel
                                ? "error-border"
                                : ""
                            }`}>
                            <SelectDropdown
                              options={
                                trainerLevelData?.data?.map((level) => ({
                                  label: level.name,
                                  value: level.id,
                                })) || []
                              }
                              selectedOption={session.trainerLevel}
                              setSelectedOption={(value) =>
                                handleSessionChange(
                                  sessionIndex,
                                  "trainerLevel",
                                  value
                                )
                              }
                              name="Trainer Level"
                              isLoading={isLoadingTrainerLevel}
                              isDisable={isDisabled}
                            />
                          </div>
                          {formErrors.sessions?.[sessionIndex]
                            ?.trainerLevel && (
                            <div className="error-text">
                              {formErrors.sessions[sessionIndex].trainerLevel}
                            </div>
                          )}
                        </div>

                        <div className="form-group checkbox-container">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={session.isTrialSession}
                              onChange={(e) =>
                                handleSessionChange(
                                  sessionIndex,
                                  "isTrialSession",
                                  e.target.checked
                                )
                              }
                              disabled={isDisabled}
                            />
                            <span>Is Trial Session</span>
                          </label>
                        </div>
                      </div>

                      <div className="form-row three-columns">
                        <div className="form-group">
                          <label>
                            Number of Sessions{" "}
                            <span className="required-mark">*</span>
                          </label>
                          <input
                            type="text"
                            value={session.numSessions}
                            onChange={(e) =>
                              handleSessionChange(
                                sessionIndex,
                                "numSessions",
                                e.target.value
                              )
                            }
                            className={`form-input ${
                              formErrors.sessions?.[sessionIndex]?.numSessions
                                ? "error-border"
                                : ""
                            }`}
                            disabled={isDisabled}
                            placeholder="Enter number"
                          />
                          {formErrors.sessions?.[sessionIndex]?.numSessions && (
                            <div className="error-text">
                              {formErrors.sessions[sessionIndex].numSessions}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label>
                            Duration (minutes){" "}
                            <span className="required-mark">*</span>
                          </label>
                          <input
                            type="text"
                            value={session.duration}
                            onChange={(e) =>
                              handleSessionChange(
                                sessionIndex,
                                "duration",
                                e.target.value
                              )
                            }
                            className={`form-input ${
                              formErrors.sessions?.[sessionIndex]?.duration
                                ? "error-border"
                                : ""
                            }`}
                            disabled={isDisabled}
                            placeholder="Enter minutes"
                          />
                          {formErrors.sessions?.[sessionIndex]?.duration && (
                            <div className="error-text">
                              {formErrors.sessions[sessionIndex].duration}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label>
                            Base Price <span className="required-mark">*</span>
                          </label>
                          <div className="input-with-icon">
                            <span className="currency-symbol">₹</span>
                            <input
                              type="text"
                              value={session.basePrice}
                              onChange={(e) =>
                                handleSessionChange(
                                  sessionIndex,
                                  "basePrice",
                                  e.target.value
                                )
                              }
                              className={`form-input with-prefix ${
                                formErrors.sessions?.[sessionIndex]?.basePrice
                                  ? "error-border"
                                  : ""
                              }`}
                              disabled={isDisabled}
                              placeholder="Enter amount"
                            />
                          </div>
                          {formErrors.sessions?.[sessionIndex]?.basePrice && (
                            <div className="error-text">
                              {formErrors.sessions[sessionIndex].basePrice}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-row two-columns">
                        <div className="form-group">
                          <label>Tax Percentage</label>
                          <div className="input-with-icon">
                            <input
                              type="text"
                              value={session.taxPercentage}
                              onChange={(e) =>
                                handleSessionChange(
                                  sessionIndex,
                                  "taxPercentage",
                                  e.target.value
                                )
                              }
                              className="form-input"
                              disabled={isDisabled}
                              placeholder="Enter percentage"
                            />
                            <span className="percent-symbol">%</span>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Final Base Price</label>
                          <div className="input-with-icon disabled">
                            <span className="currency-symbol">₹</span>
                            <input
                              type="text"
                              value={session.finalBasePrice}
                              className="form-input with-prefix"
                              disabled
                              placeholder="Calculated amount"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Selling Prices Section */}
                      <div className="selling-prices">
                        <div className="selling-prices-header">
                          <div className="selling-prices-title">
                            Selling Prices
                          </div>
                          <button
                            type="button"
                            className="add-price-btn"
                            onClick={() => addSellingPrice(sessionIndex)}
                            disabled={isDisabled}>
                            <span>+ Add Price Option</span>
                          </button>
                        </div>

                        {/* First selling price (always visible) */}
                        {session.sellingPrices.length > 0 && (
                          <div className="selling-price-item primary">
                            <div className="selling-price-header">
                              <div className="selling-price-name">
                                <span className="green-dot"></span>
                                <span>Selling Price Option 1</span>
                              </div>

                              {session.sellingPrices.length > 1 && (
                                <button
                                  type="button"
                                  className="toggle-prices-btn"
                                  onClick={() =>
                                    setShowAdditionalSellingPrices(
                                      !showAdditionalSellingPrices
                                    )
                                  }>
                                  {showAdditionalSellingPrices ? (
                                    <>
                                      Hide {session.sellingPrices.length - 1}{" "}
                                      Additional Options{" "}
                                      <span className="arrow">▲</span>
                                    </>
                                  ) : (
                                    <>
                                      Show {session.sellingPrices.length - 1}{" "}
                                      Additional Options{" "}
                                      <span className="arrow">▼</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>

                            <div className="selling-price-body">
                              <div className="form-row three-columns">
                                <div className="form-group">
                                  <label>
                                    Final Selling Price{" "}
                                    <span className="required-mark">*</span>
                                  </label>
                                  <div className="input-with-icon">
                                    <span className="currency-symbol">₹</span>
                                    <input
                                      type="text"
                                      value={
                                        session.sellingPrices[0]
                                          .finalSellingPrice
                                      }
                                      onChange={(e) =>
                                        handleSellingPriceChange(
                                          sessionIndex,
                                          0,
                                          "finalSellingPrice",
                                          e.target.value
                                        )
                                      }
                                      className={`form-input with-prefix ${
                                        formErrors.sessions?.[sessionIndex]
                                          ?.sellingPrices?.[0]
                                          ?.finalSellingPrice
                                          ? "error-border"
                                          : ""
                                      }`}
                                      disabled={isDisabled}
                                      placeholder="Enter amount"
                                    />
                                  </div>
                                  {formErrors.sessions?.[sessionIndex]
                                    ?.sellingPrices?.[0]?.finalSellingPrice && (
                                    <div className="error-text">
                                      {
                                        formErrors.sessions[sessionIndex]
                                          .sellingPrices[0].finalSellingPrice
                                      }
                                    </div>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label>Tax Percentage</label>
                                  <div className="input-with-icon">
                                    <input
                                      type="text"
                                      value={
                                        session.sellingPrices[0].taxPercentage
                                      }
                                      onChange={(e) =>
                                        handleSellingPriceChange(
                                          sessionIndex,
                                          0,
                                          "taxPercentage",
                                          e.target.value
                                        )
                                      }
                                      className="form-input"
                                      disabled={isDisabled}
                                      placeholder="Enter percentage"
                                    />
                                    <span className="percent-symbol">%</span>
                                  </div>
                                </div>

                                <div className="form-group">
                                  <label>Base Price</label>
                                  <div className="input-with-icon disabled">
                                    <span className="currency-symbol">₹</span>
                                    <input
                                      type="text"
                                      value={session.sellingPrices[0].basePrice}
                                      className="form-input with-prefix"
                                      disabled
                                      placeholder="Calculated amount"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Additional selling prices (collapsible) */}
                        {session.sellingPrices.length > 1 && (
                          <div
                            className={`additional-selling-prices ${
                              showAdditionalSellingPrices
                                ? "expanded"
                                : "collapsed"
                            }`}>
                            {session.sellingPrices
                              .slice(1)
                              .map((sp, spIndex) => {
                                const actualIndex = spIndex + 1;
                                return (
                                  <div
                                    key={spIndex}
                                    className="selling-price-item">
                                    <div className="selling-price-header secondary">
                                      <div className="selling-price-name">
                                        Selling Price Option {actualIndex + 1}
                                      </div>
                                      <button
                                        className="remove-icon"
                                        type="button"
                                        onClick={() =>
                                          removeSellingPrice(
                                            sessionIndex,
                                            actualIndex
                                          )
                                        }
                                        disabled={isDisabled}>
                                        <span>×</span>
                                      </button>
                                    </div>

                                    <div className="selling-price-body">
                                      <div className="form-row three-columns">
                                        <div className="form-group">
                                          <label>
                                            Final Selling Price{" "}
                                            <span className="required-mark">
                                              *
                                            </span>
                                          </label>
                                          <div className="input-with-icon">
                                            <span className="currency-symbol">
                                              ₹
                                            </span>
                                            <input
                                              type="text"
                                              value={sp.finalSellingPrice}
                                              onChange={(e) =>
                                                handleSellingPriceChange(
                                                  sessionIndex,
                                                  actualIndex,
                                                  "finalSellingPrice",
                                                  e.target.value
                                                )
                                              }
                                              className={`form-input with-prefix ${
                                                formErrors.sessions?.[
                                                  sessionIndex
                                                ]?.sellingPrices?.[actualIndex]
                                                  ?.finalSellingPrice
                                                  ? "error-border"
                                                  : ""
                                              }`}
                                              disabled={isDisabled}
                                              placeholder="Enter amount"
                                            />
                                          </div>
                                          {formErrors.sessions?.[sessionIndex]
                                            ?.sellingPrices?.[actualIndex]
                                            ?.finalSellingPrice && (
                                            <div className="error-text">
                                              {
                                                formErrors.sessions[
                                                  sessionIndex
                                                ].sellingPrices[actualIndex]
                                                  .finalSellingPrice
                                              }
                                            </div>
                                          )}
                                        </div>

                                        <div className="form-group">
                                          <label>Tax Percentage</label>
                                          <div className="input-with-icon">
                                            <input
                                              type="text"
                                              value={sp.taxPercentage}
                                              onChange={(e) =>
                                                handleSellingPriceChange(
                                                  sessionIndex,
                                                  actualIndex,
                                                  "taxPercentage",
                                                  e.target.value
                                                )
                                              }
                                              className="form-input"
                                              disabled={isDisabled}
                                              placeholder="Enter percentage"
                                            />
                                            <span className="percent-symbol">
                                              %
                                            </span>
                                          </div>
                                        </div>

                                        <div className="form-group">
                                          <label>Base Price</label>
                                          <div className="input-with-icon disabled">
                                            <span className="currency-symbol">
                                              ₹
                                            </span>
                                            <input
                                              type="text"
                                              value={sp.basePrice}
                                              className="form-input with-prefix"
                                              disabled
                                              placeholder="Calculated amount"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CentralisedPTForm;
